import { FALLBACK_POINTS_ON_ZERO_WIN } from '../prize-box-page.constants';
import { GamePhase, HandType, PrizeKind, PrizeResult, RoundResult } from '../prize-box-page.types';
import { mapHandToMove, mapStatusToOutcome } from '../prize-box-page.utils';
import * as api from '../services/raffle-adapter';
import { buildPrizeBoxMockPatch } from 'e2e-tests/desktop/prize-crate/prize-crate-mock';
import { PRIZE_CRATE_MOCK_DATA } from 'e2e-tests/mocks/wagmi.mocks';
import { PublicClient, WalletClient } from 'viem';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { MovesEnum, RoundOutcome } from 'shared/constants/shared-enums';
import { useQuestsStore } from 'shared/store/questsStore';
import { isMocked } from 'shared/utils/isMocked';

export interface IPrizeBoxState {
  poolUsdt: number;
  recoveryAmount: number;
  quotaResetAt: number | null;
  extraCredits: number;
  currentSlotBalance: number;
  attempts: number;
  attemptsLeft: number;
  needsRestore: boolean;
  contractReadable: boolean;

  // UI
  phase: GamePhase;
  selectedMove: MovesEnum | null;
  aiMove: MovesEnum | null;
  result: PrizeResult | null;
  playerWins: number;
  aiWins: number;
  lastMoves: { playerAMove: MovesEnum; playerBMove: MovesEnum } | null;
  lastRoundOutcome: RoundOutcome | null;
  pool: number;

  isPrizeBoxLoading: boolean;
  prizeBoxError: string | null;

  setPrizeBoxStore: (u: Partial<IPrizeBoxState>) => void;
  refreshPrizeBoxStore: (publicClient: PublicClient, address: `0x${string}`) => Promise<void>;

  handleSmash: (
    hand: HandType,
    publicClient: PublicClient,
    walletClient: WalletClient,
    address: `0x${string}`,
    canPlayNow: boolean,
  ) => Promise<void>;
}

export const usePrizeBoxStore = create<IPrizeBoxState>()(
  devtools((set, get) => ({
    poolUsdt: 0,
    recoveryAmount: 0,
    quotaResetAt: null,
    extraCredits: 0,
    currentSlotBalance: 0,
    attempts: 0,
    attemptsLeft: 0,
    needsRestore: true,
    contractReadable: true,
    isPrizeBoxLoading: false,
    prizeBoxError: null,

    //Additional fields for UI
    phase: GamePhase.Idle,
    selectedMove: null,
    aiMove: null,
    result: null,
    playerWins: 0,
    aiWins: 0,
    lastMoves: null,
    lastRoundOutcome: null,
    pool: 0,

    setPrizeBoxStore: (u) =>
      set((prev) => {
        const next = { ...prev, ...u };
        if ('attempts' in u || 'extraCredits' in u) {
          next.attemptsLeft = Math.max(0, next.attempts) + Math.max(0, next.extraCredits);
        }
        return next;
      }),

    refreshPrizeBoxStore: async (publicClient, address) => {
      if (!publicClient || !address) return;

      set({ isPrizeBoxLoading: true, prizeBoxError: null });

      try {
        if (isMocked) {
          const patch = buildPrizeBoxMockPatch(PRIZE_CRATE_MOCK_DATA);
          get().setPrizeBoxStore(patch); // derives attemptsLeft
          set({ isPrizeBoxLoading: false });
          return;
        }

        const state = await api.getState(publicClient, address);
        set({
          poolUsdt: state.poolUsdt,
          recoveryAmount: state.recoveryAmount,
          quotaResetAt: state.quotaResetAt,
          extraCredits: state.extraCredits,
          currentSlotBalance: state.currentSlotBalance,
          attempts: state.attempts,
          needsRestore: state.needsRestore,
          contractReadable: state.contractReadable,
          attemptsLeft: Math.max(0, state.attempts) + Math.max(0, state.extraCredits),
        });
      } catch (e: any) {
        set({ prizeBoxError: e?.message || String(e) });
      } finally {
        set({ isPrizeBoxLoading: false });
      }
    },

    handleSmash: async (hand, publicClient, walletClient, address, canPlayNow) => {
      const { fetchUserBalances, setSnapshotPoints } = useQuestsStore.getState();

      if (!address || !publicClient || !walletClient) {
        set({ phase: GamePhase.Idle });
        return;
      }
      if (!canPlayNow) {
        set({ phase: GamePhase.Cooldown });
        return;
      }

      set({ phase: GamePhase.Revealing, prizeBoxError: null });
      setSnapshotPoints();

      try {
        const resp = await api.play(hand, publicClient, walletClient, address);

        const ai = resp.aiHand ?? null;
        const playerMove = mapHandToMove(hand);
        const aiMove = mapHandToMove(ai);

        let result: PrizeResult;
        if (resp.status === RoundResult.Win) {
          if (resp.winAmount > 0) {
            result = {
              kind: PrizeKind.Money,
              amount: resp.winAmount,
              totalAmount: resp.totalAmount,
              status: RoundResult.Win,
              potCents: (resp as any).potCents,
            } as PrizeResult;
          } else {
            result = {
              kind: PrizeKind.Points,
              amount: FALLBACK_POINTS_ON_ZERO_WIN,
              status: RoundResult.Win,
            };
          }
          set({ playerWins: 1, aiWins: 0 });
        } else if (resp.status === RoundResult.Draw) {
          result = { kind: PrizeKind.Draw, amount: 0, status: RoundResult.Draw };
          set({ playerWins: 0, aiWins: 0 });
        } else {
          result = { kind: PrizeKind.Lose, amount: 0, status: RoundResult.Lose };
          set({ playerWins: 0, aiWins: 1 });
        }

        get().setPrizeBoxStore({
          attempts: resp.state.attempts,
          extraCredits: resp.state.extraCredits,
          pool: resp.state.poolUsdt,
          quotaResetAt: resp.state.quotaResetAt,
          result,
          aiMove,
          selectedMove: playerMove,
          lastMoves: { playerAMove: playerMove, playerBMove: aiMove },
          lastRoundOutcome: mapStatusToOutcome(resp.status),
        });

        setTimeout(async () => {
          set({ phase: GamePhase.Result });
          await fetchUserBalances(publicClient, address);
        }, 1000);
      } catch (e: any) {
        console.error('play() failed:', e);

        const msg = String(e?.message || '').toLowerCase();

        set({
          selectedMove: null,
          aiMove: null,
          playerWins: 0,
          aiWins: 0,
          prizeBoxError: 'Transaction failed: ' + msg,
        });

        if (msg.includes('user denied') || msg.includes('user rejected')) {
          set({ phase: GamePhase.Idle, prizeBoxError: 'Transaction was rejected by the user.' });
          return;
        }

        if (msg.includes('callcountexceeded') || msg.includes('cooldown')) {
          set({ phase: GamePhase.Cooldown });
        } else {
          set({ phase: GamePhase.Idle });
        }
      }
    },
  })),
);
